import { useState } from 'react'
import { X } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import type { CanvasSlot } from '../editor-shared/canvasTypes'
import type { BlockContent, PagePool, PageKind } from '../../api/types'
import Navbar from '../navbar'
import { useSlotArticles, PageSlotRenderer } from '../page-renderer/PageSlotRenderer'
import { usePagePoolArticles } from '../../api/hooks/useArticles'
import { useTopics, useRegions } from '../../hooks/useTaxonomy'

// ─── PagePreview ──────────────────────────────────────────────────────────────

export interface PagePreviewProps {
  slots: CanvasSlot[]
  contentMap: Record<string, BlockContent>
  pageTitle: string
  pool?: PagePool
  pageKind?: PageKind
  pageSlug?: string
  /** 'overlay' (default) — rendered inside a Dialog, includes a Dialog.Close button.
   *  'popout' — rendered as a standalone full page in its own window. */
  mode?: 'overlay' | 'popout'
}

export function PagePreview({ slots, contentMap, pageTitle, pool, pageKind, pageSlug, mode = 'overlay' }: PagePreviewProps) {
  const isDefaultTemplate = pageSlug === 'default' && (pageKind === 'topic' || pageKind === 'region')
  const [previewSlug, setPreviewSlug] = useState<string | undefined>(undefined)
  const { data: topics = [] } = useTopics()
  const { data: regions = [] } = useRegions()
  const taxonomy = pageKind === 'topic' ? topics : regions

  const effectivePool: PagePool | undefined = pool
    ? (isDefaultTemplate && previewSlug
        ? { ...pool, ...(pageKind === 'topic' ? { topicSlug: previewSlug } : { regionSlug: previewSlug }) }
        : pool)
    : undefined

  const activeSlug = isDefaultTemplate ? previewSlug : pageSlug
  const sectionLabel = (pageKind === 'topic' || pageKind === 'region') && activeSlug
    ? taxonomy.find((t) => t.slug === activeSlug)?.label
    : undefined

  const { articles: poolArticles = [] } = usePagePoolArticles(effectivePool)
  const slotArticleMap = useSlotArticles(slots, contentMap, effectivePool ? poolArticles : undefined)
  const isPopout = mode === 'popout'

  return (
    <div className={isPopout ? 'flex h-screen flex-col bg-canvas' : 'fixed inset-0 z-50 flex flex-col bg-canvas'}>

      {/* Preview bar — close button differs between overlay and popout */}
      <div className="flex shrink-0 items-center justify-between border-b border-line bg-accent-tertiary-fill px-6 py-2">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-widest text-fg-secondary">Page Preview</span>
          <span className="truncate text-xs text-fg-secondary">— {pageTitle}</span>
          {isDefaultTemplate && taxonomy.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-fg-secondary">Preview as:</span>
              <select
                value={previewSlug ?? ''}
                onChange={(e) => setPreviewSlug(e.target.value || undefined)}
                className="rounded border border-line bg-canvas px-2 py-1 text-xs text-fg"
              >
                <option value="">— pick a {pageKind} —</option>
                {taxonomy.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        {isPopout ? (
          <button
            type="button"
            onClick={() => window.close()}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
          >
            <X size={12} aria-hidden="true" />
            Close Window
          </button>
        ) : (
          <Dialog.Close asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
            >
              <X size={12} aria-hidden="true" />
              Close Preview
            </button>
          </Dialog.Close>
        )}
      </div>

      {/* Page content — scrollable, reader shell included */}
      <div className="flex-1 overflow-y-auto">
        <Navbar />
        <div className="mx-auto max-w-7xl px-6 pb-12">
          {sectionLabel && (
            <div className="mb-8 border-b border-line pb-5 pt-6">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">
                {pageKind === 'topic' ? 'Topic' : 'Region'}
              </div>
              <h1 className="mt-1 font-zodiak text-4xl font-bold text-fg">{sectionLabel}</h1>
            </div>
          )}
          {slots.length === 0 ? (
            <div className="flex h-48 items-center justify-center text-sm text-fg-secondary">
              No blocks in this page yet.
            </div>
          ) : (
            <div className="space-y-10">
              {slots.map((slot) => (
                <PageSlotRenderer
                  key={slot.id}
                  slot={slot}
                  articles={slotArticleMap[slot.id] ?? []}
                  slotArticleMap={slotArticleMap}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
